import { Link } from 'react-router-dom';
import type { Bot } from '../../types';
import { StatusIndicator } from '../common/StatusIndicator';
import { getEnvironmentColor, getProfitColor } from '../../styles/theme';
import { useBotMetrics } from '../../hooks/useBots';
import { SparklineChart } from './SparklineChart';

/**
 * Convert bot API URL to use current browser hostname instead of localhost.
 */
function getFreqUIUrl(apiUrl: string | undefined): string | null {
  if (!apiUrl) return null;
  try {
    const url = new URL(apiUrl);
    // Replace localhost/127.0.0.1 with current browser hostname
    if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
      url.hostname = window.location.hostname;
    }
    return url.toString();
  } catch {
    return apiUrl;
  }
}

interface BotCardProps {
  bot: Bot;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

function formatNumber(num: number, decimals: number = 2): string {
  return num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function BotCard({ bot, isExpanded, onToggleExpand }: BotCardProps) {
  const { data: metrics, isLoading: metricsLoading } = useBotMetrics(bot.id);

  const profitValue = metrics?.profit_abs ?? 0;
  const profitColor = getProfitColor(profitValue);
  const envColor = getEnvironmentColor(bot.environment);

  return (
    <div className="bot-card transition-shadow hover:shadow-md">
      {/* Card Header */}
      <div className="bot-card-header p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <StatusIndicator status={bot.health_state} size="lg" pulse />
            <div>
              <Link
                to={`/bots/${bot.id}`}
                className="text-lg font-semibold text-[color:var(--surface-text)] hover:text-[color:var(--surface-border-strong)]"
              >
                {bot.name}
              </Link>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={`industrial-pill px-2 py-0.5 text-xs rounded-full ${envColor}`}
                >
                  {bot.environment}
                </span>
                {bot.is_dryrun && (
                  <span className="industrial-pill px-2 py-0.5 text-xs rounded-full">
                    Dry Run
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {/* FreqUI Direct Link */}
            {getFreqUIUrl(bot.api_url) && (
              <a
                href={getFreqUIUrl(bot.api_url)!}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 text-[color:var(--surface-text-muted)] hover:text-[color:var(--surface-border-strong)] hover:bg-[color:var(--nav-hover-bg)] rounded transition-colors"
                title="Open FreqUI"
                onClick={(e) => e.stopPropagation()}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            )}

            {/* Mobile expand button */}
            <button
              onClick={onToggleExpand}
              className="lg:hidden p-1 text-[color:var(--surface-text-muted)] hover:text-[color:var(--surface-text)]"
            >
              <svg
                className={`w-5 h-5 transition-transform ${
                  isExpanded ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Card Body */}
      <div className={`p-4 ${isExpanded ? 'block' : 'hidden lg:block'}`}>
        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          {/* Profit */}
          <div className="metric-panel p-3">
            <p className="shell-muted text-xs">Profit</p>
            {metricsLoading ? (
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            ) : (
              <p className={`text-lg font-semibold ${profitColor}`}>
                {profitValue >= 0 ? '+' : ''}
                {formatNumber(profitValue)} USDT
              </p>
            )}
          </div>

          {/* Win Rate */}
          <div className="metric-panel p-3">
            <p className="shell-muted text-xs">Win Rate</p>
            {metricsLoading ? (
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            ) : (
              <p className="text-lg font-semibold text-[color:var(--surface-text)]">
                {metrics?.win_rate
                  ? `${formatNumber(metrics.win_rate * 100)}%`
                  : 'N/A'}
              </p>
            )}
          </div>

          {/* Open Positions */}
          <div className="metric-panel p-3">
            <p className="shell-muted text-xs">Positions</p>
            {metricsLoading ? (
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            ) : (
              <p className="text-lg font-semibold text-[color:var(--surface-text)]">
                {metrics?.open_positions ?? 0}
              </p>
            )}
          </div>

          {/* Trades */}
          <div className="metric-panel p-3">
            <p className="shell-muted text-xs">Trades</p>
            {metricsLoading ? (
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            ) : (
              <p className="text-lg font-semibold text-[color:var(--surface-text)]">
                {metrics?.closed_trades ?? 0}
              </p>
            )}
          </div>
        </div>

        {/* Mini Chart */}
        <div className="h-12 mb-4">
          <SparklineChart botId={bot.id} />
        </div>

        {/* Tags & Info */}
        <div className="flex flex-wrap gap-2 text-xs shell-muted">
          {bot.exchange && (
            <span className="industrial-pill px-2 py-1 rounded">
              {bot.exchange}
            </span>
          )}
          {bot.strategy && (
            <span
              className="industrial-pill truncate max-w-[120px] px-2 py-1 rounded"
              title={bot.strategy}
            >
              {bot.strategy}
            </span>
          )}
          {bot.tags.map((tag) => (
            <span
              key={tag}
              className="industrial-pill px-2 py-1 rounded"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Data source indicator */}
        {metrics && (
          <div className="industrial-divider mt-3 border-t pt-3">
            <span
              className={`text-xs ${
                metrics.data_source === 'api'
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-yellow-600 dark:text-yellow-400'
              }`}
            >
              Data: {metrics.data_source.toUpperCase()}
            </span>
          </div>
        )}
      </div>

      {/* Card Footer - Quick Actions */}
      <div
        className={`bot-card-footer px-4 py-3 flex justify-between items-center ${
          isExpanded ? 'block' : 'hidden lg:flex'
        }`}
      >
        <Link
          to={`/bots/${bot.id}`}
          className="text-sm text-[color:var(--surface-border-strong)] hover:underline"
        >
          View Details
        </Link>
        {bot.last_seen && (
          <span className="shell-muted text-xs">
            Last seen: {new Date(bot.last_seen).toLocaleTimeString('de-AT', { timeZone: 'Europe/Vienna' })}
          </span>
        )}
      </div>
    </div>
  );
}
